/**
 * AI money, from integer micros to what a person reads — Story 23.3.
 *
 * DECISION-008: "Money is integer cents. AI cost is integer micros." Story 23.3's
 * criterion is that money is "formatted from integer micros with no float
 * arithmetic anywhere in the path", so nothing here divides a number by a number.
 * Every division is BigInt, and a figure becomes digits by string rather than by
 * `toFixed`, which rounds the float it is handed: `(1.005).toFixed(2)` is `"1.00"`,
 * and 1,005,000 micros is `$1.01`. `test/money.test.ts` holds that case, and
 * `tests/usage-money-integer.test.ts` scans the path for the operators that would
 * undo it.
 *
 * The currency is dollars because AI cost is priced and stored in dollar micros
 * (`config` `ai.rates`, PRD §3.12's "$0.011 per 1,000 Neurons").
 */

/** How many micros make a cent — a unit, not a rate. */
const PER_CENT = 10_000n

/** Half up, in BigInt. `n` and `d` are whole; `d` is positive. */
const divideHalfUp = (n: bigint, d: bigint): bigint => (n * 2n + d) / (d * 2n)

/** Thousands separated, the page's own `toLocaleString` convention, from digits alone. */
function grouped(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** `$34.86` from 34,860,000 micros — to the cent, half up. */
export function formatMicros(micros: number): string {
  const cents = divideHalfUp(BigInt(micros), PER_CENT)
  const negative = cents < 0n
  const whole = negative ? -cents : cents
  const dollars = (whole / 100n).toString()
  const remainder = (whole % 100n).toString().padStart(2, '0')
  return `${negative ? '-' : ''}$${grouped(dollars)}.${remainder}`
}

/** `$2.50`, and `$0` for nothing — the chart's value scale drops cents that are zero only at zero. */
export function formatScale(micros: number): string {
  return micros === 0 ? '$0' : formatMicros(micros)
}

/** Whole percent, half up — `part` of `whole`, or 0 when there is no whole. */
export function percentOf(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Number(divideHalfUp(BigInt(part) * 100n, BigInt(whole)))
}

/** `total / count`, half up, in micros — an average day, or a lead's share. */
export function averageMicros(total: number, count: number): number {
  if (count <= 0) return 0
  return Number(divideHalfUp(BigInt(total), BigInt(count)))
}

/**
 * The top of a chart's value scale: the smallest step at or above `max` whose half
 * is still a whole cent — `$2.50` for a highest day of `$2.41`, so the middle label
 * reads `$1.25`. Steps run 1 · 2 · 5 below a dollar and 1 · 2 · 2.5 · 5 from a
 * dollar up, because a quarter of a dime has no whole-cent half.
 */
export function scaleTop(maxMicros: number): number {
  const max = BigInt(Math.max(0, Math.trunc(maxMicros)))
  for (let decade = 100_000n; ; decade *= 10n) {
    const steps = decade >= 1_000_000n ? [10n, 20n, 25n, 50n] : [10n, 20n, 50n]
    for (const step of steps) {
      const top = (decade * step) / 10n
      if (top >= max) return Number(top)
    }
  }
}

/** How tall a bar is, as a whole percent of the scale's top — at least 1 for any spend at all. */
export function barPercent(micros: number, topMicros: number): number {
  if (micros <= 0 || topMicros <= 0) return 0
  const top = BigInt(topMicros)
  return Number((BigInt(micros) * 100n + top - 1n) / top)
}

/** Half of an amount, to the micro, in BigInt — the chart's middle label. */
export const halfOf = (micros: number): number => Number(BigInt(Math.trunc(micros)) / 2n)

/* ── budget caps — Story 23.4 ───────────────────────────────────────────── */

/**
 * A cap as a sentence says it: `$30`, or `$30.50` when it has cents — the export's
 * "You passed your $30 warning".
 */
export function formatCap(micros: number): string {
  const text = formatMicros(micros)
  return text.endsWith('.00') ? text.slice(0, -3) : text
}

/**
 * What an operator typed, in micros — from its digits, never through a float.
 *
 * `null` is an empty field, which is no cap. `undefined` is text that is not an
 * amount of dollars and cents, which the card refuses before anything is sent:
 * `30.505` has a fraction of a cent, `1e3` is an exponent a number input accepts,
 * and more than nine digits of dollars is past the integers a micro amount can hold
 * exactly. Zero is left to the API, whose refusal says what to do instead.
 */
export function dollarsToMicros(text: string): number | null | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return undefined
  const cents = BigInt(match[1]!) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'))
  return Number(cents * PER_CENT)
}

/** A stored cap back into its field: `30`, or `30.50`. Caps are whole cents. */
export function microsToDollars(micros: number): string {
  const cents = BigInt(Math.trunc(micros)) / PER_CENT
  const dollars = (cents / 100n).toString()
  const remainder = cents % 100n
  return remainder === 0n ? dollars : `${dollars}.${remainder.toString().padStart(2, '0')}`
}
